import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedPage from '../components/AnimatedPage';
import { showMessage, postRequest } from '../config';
import { PROFILE } from '../constants/routes';

const ConfirmEmailChange = () => {
    const navigate = useNavigate();
    const userId = window.location.pathname.split('/').pop();
    const type = new URLSearchParams(window.location.search).get('type') || 'email';

    const [state, setState] = useState({
        loading: true,
        success: false,
        error: null
    });

    const confirmEmailChange = async (userId) => {
        try {
            setState({ loading: true, success: false, error: null });

            const response = await postRequest(`/user/confirmEmailChange/${userId}?type=${type}`, {});

            if (response && response.code === 200) {
                setState({ loading: false, success: true, error: null });
                showMessage("Email change confirmed successfully.");

                // Redirect to profile after 2 seconds
                setTimeout(() => {
                    // navigate(PROFILE);
                    window.location.href = PROFILE;
                }, 2000);
            } else {
                const errorMsg = response?.message || "Failed to confirm email change. Please try again.";
                setState({ loading: false, success: false, error: errorMsg });
            }
        } catch (error) {
            console.error("Error confirming email change:", error);
            const errorMsg = "An error occurred while confirming email change.";
            setState({ loading: false, success: false, error: errorMsg });
        }
    };

    useEffect(() => {
        if (userId) {
            confirmEmailChange(userId);
        } else {
            setState({ loading: false, success: false, error: "Invalid confirmation link." });
        }
    }, [userId]);

    const LoadingDots = () => (
        <motion.div
            className="df aic jcc"
            style={{ gap: '8px' }}
        >
            {[0, 1, 2].map((index) => (
                <motion.div
                    key={index}
                    className="br100"
                    style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: '#FC7785'
                    }}
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.7, 1, 0.7]
                    }}
                    transition={{
                        duration: 1,
                        repeat: Infinity,
                        delay: index * 0.2
                    }}
                />
            ))}
        </motion.div>
    );

    return (
        <AnimatedPage>
            <div className="mih100vh df aic jcc bg5">
                <div className="container df fdc aic tac">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="df fdc aic bg1 p3rem br25px"
                        style={{
                            maxWidth: '500px',
                            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                            minHeight: '300px'
                        }}
                    >
                        {/* Icon */}
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, duration: 0.5 }}
                            className="mb2rem"
                        >
                            {state.loading && (
                                <div
                                    className="df aic jcc br100 mb1rem"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        backgroundColor: '#FC778520'
                                    }}
                                >
                                    <svg
                                        width="40"
                                        height="40"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        style={{ color: '#FC7785' }}
                                    >
                                        <path
                                            stroke="currentColor"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                            )}

                            {state.success && (
                                <div
                                    className="df aic jcc br100 mb1rem"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        backgroundColor: '#40dda120'
                                    }}
                                >
                                    <svg
                                        width="40"
                                        height="40"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        style={{ color: '#40dda1' }}
                                    >
                                        <path
                                            stroke="currentColor"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                            )}

                            {state.error && (
                                <div
                                    className="df aic jcc br100 mb1rem"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        backgroundColor: '#dd404020'
                                    }}
                                >
                                    <svg
                                        width="40"
                                        height="40"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        style={{ color: '#dd4040' }}
                                    >
                                        <path
                                            stroke="currentColor"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                            )}
                        </motion.div>

                        {/* Content */}
                        <div className="df fdc aic">
                            {state.loading && (
                                <>
                                    <motion.h1
                                        className="c6 fw600 fs2rem mb1rem tac"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.3 }}
                                    >
                                        Email Change Confirmation
                                    </motion.h1>

                                    <motion.div
                                        className="df fdc aic"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.4 }}
                                    >
                                        <p className="c6 fs1-125rem mb2rem tac op75">
                                            Processing your email change confirmation
                                        </p>
                                        <LoadingDots />
                                    </motion.div>
                                </>
                            )}

                            {state.success && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5 }}
                                    className="df fdc aic tac"
                                >
                                    <h1 className="c6 fw600 fs2rem mb1rem">
                                        Success!
                                    </h1>
                                    <p className="c6 fs1-125rem mb2rem op75">
                                        Your email change has been confirmed successfully.
                                    </p>
                                    <p className="c4 fs1rem fw500">
                                        Redirecting to your profile...
                                    </p>
                                </motion.div>
                            )}

                            {state.error && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5 }}
                                    className="df fdc aic tac"
                                >
                                    <h1 className="c6 fw600 fs2rem mb1rem">
                                        Confirmation Failed
                                    </h1>
                                    <p className="c9 fs1-125rem mb2rem tac">
                                        {state.error}
                                    </p>
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="px2rem py1rem c1 fw600 br25px td300 bss bw0px cp"
                                        style={{ backgroundColor: '#FC7785' }}
                                        onClick={() => window.location.reload()}
                                    >
                                        Try Again
                                    </motion.button>
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>
        </AnimatedPage>
    );
};

export default ConfirmEmailChange;